import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ApiFailure,
  api,
  type CalendarSyncFailure,
  type LadderClass,
  type LadderRung,
  type UpcomingReminder,
} from '../lib/api';

/**
 * The schedule — P4-6, §4.4.
 *
 * Three things a person needs from an engine that otherwise runs on a timer:
 * what is coming, what failed to reach the calendar, and what the ladders are
 * set to.
 *
 * The upcoming list reads pending reminder rows rather than re-deriving
 * deadlines from the records. That is deliberate: it shows what the engine will
 * actually deliver, including the absence of a rung that was already in the
 * past when the date was entered. A list that recomputed the ladder here would
 * promise warnings that are never going to arrive.
 *
 * §0.1: white and #F4F6FA surfaces, navy text, no gold on any light surface.
 * Overdue and failed read in danger red, imminent in warning amber.
 */

const DAY = 1440;

/** Lead time in the words a person reads on a lock screen. */
function leadLabel(offsetMinutes: number | null): string {
  if (offsetMinutes === null || offsetMinutes <= 0) return 'On the day';
  if (offsetMinutes < 60) return `${offsetMinutes} min before`;
  if (offsetMinutes < DAY) {
    const h = Math.round(offsetMinutes / 60);
    return `${h} hour${h === 1 ? '' : 's'} before`;
  }
  const d = Math.round(offsetMinutes / DAY);
  return `${d} day${d === 1 ? '' : 's'} before`;
}

function whenLabel(iso: string): string {
  const then = new Date(iso).getTime();
  const mins = Math.round((then - Date.now()) / 60_000);
  if (mins < 0) return 'overdue';
  if (mins < 60) return `in ${mins} min`;
  if (mins < DAY) return `in ${Math.round(mins / 60)} h`;
  return `in ${Math.round(mins / DAY)} d`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Rows for one deadline, collapsed. One deadline, several rungs. */
interface Deadline {
  key: string;
  title: string;
  label: string | null;
  projectName: string | null;
  dueAt: string;
  entityId: string | null;
  rungs: UpcomingReminder[];
  recipientCount: number;
}

function groupByDeadline(rows: UpcomingReminder[]): Deadline[] {
  const map = new Map<string, Deadline>();
  for (const r of rows) {
    const key = `${r.entityType}:${r.entityId}:${r.dueAt}`;
    const existing = map.get(key);
    if (existing) {
      existing.rungs.push(r);
    } else {
      map.set(key, {
        key,
        title: r.title,
        label: r.label,
        projectName: r.projectName,
        dueAt: r.dueAt,
        entityId: r.entityId,
        rungs: [r],
        recipientCount: r.recipientCount,
      });
    }
  }
  return [...map.values()].sort((a, b) => Date.parse(a.dueAt) - Date.parse(b.dueAt));
}

export function SchedulePanel(): JSX.Element {
  const [reminders, setReminders] = useState<UpcomingReminder[]>([]);
  const [failures, setFailures] = useState<CalendarSyncFailure[]>([]);
  const [calendarConfigured, setCalendarConfigured] = useState(true);
  const [ladders, setLadders] = useState<{ classes: LadderClass[]; rungs: LadderRung[] } | null>(
    null,
  );
  const [within, setWithin] = useState(14);
  const [mine, setMine] = useState(false);
  const [showLadders, setShowLadders] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [up, sync] = await Promise.all([
        api.upcomingReminders({ within, mine }),
        api.calendarSyncFailures(),
      ]);
      setReminders(up.reminders);
      setFailures(sync.failures);
      setCalendarConfigured(sync.calendarConfigured);
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiFailure ? err.body.message : 'The schedule could not be read.',
      );
    } finally {
      setLoading(false);
    }
  }, [within, mine]);

  useEffect(() => {
    void load();
  }, [load]);

  const openLadders = useCallback(async () => {
    setShowLadders((v) => !v);
    if (!ladders) {
      try {
        setLadders(await api.reminderLadders());
      } catch (err) {
        setError(err instanceof ApiFailure ? err.body.message : 'The ladders could not be read.');
      }
    }
  }, [ladders]);

  const retry = useCallback(
    async (f: CalendarSyncFailure) => {
      setBusy(`${f.entityType}:${f.entityId}`);
      setError(null);
      setNotice(null);
      try {
        await api.retryCalendarSync(f.entityType, f.entityId);
        setNotice('Calendar sync retried.');
        await load();
      } catch (err) {
        setError(err instanceof ApiFailure ? err.body.message : 'The retry did not go through.');
      } finally {
        setBusy(null);
      }
    },
    [load],
  );

  const deadlines = useMemo(() => groupByDeadline(reminders), [reminders]);

  return (
    <section className="space-y-4" aria-labelledby="schedule-heading">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 id="schedule-heading" className="text-h1 font-semibold text-navy">
            Schedule
          </h1>
          <p className="text-micro text-text-secondary">
            Reminders the engine will deliver, and the deadlines behind them.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/*
            A pill toggle rather than a bare checkbox: the app has no styled
            checkbox anywhere, and the browser default renders as an unstyled
            white square against the header row.
          */}
          <button
            type="button"
            onClick={() => setMine((v) => !v)}
            aria-pressed={mine}
            className={`rounded-pill border px-3 py-1 text-micro font-semibold ${
              mine
                ? 'border-royal bg-royal text-white'
                : 'border-border bg-white text-text-secondary'
            }`}
          >
            Only mine
          </button>
          <select
            value={within}
            onChange={(e) => setWithin(Number(e.target.value))}
            aria-label="Horizon"
            className="rounded-card border border-border bg-white px-2 py-1 text-micro text-text-primary"
          >
            <option value={7}>Next 7 days</option>
            <option value={14}>Next 14 days</option>
            <option value={30}>Next 30 days</option>
            <option value={90}>Next 90 days</option>
          </select>
        </div>
      </header>

      {error ? (
        <p role="alert" className="rounded-card bg-danger/10 px-3 py-2 text-micro text-danger">
          {error}
        </p>
      ) : null}
      {notice ? (
        <p className="rounded-card bg-success/10 px-3 py-2 text-micro text-success">{notice}</p>
      ) : null}

      {/*
        The warning badge §4.4 asks for. It sits above the list rather than
        beside each row: a calendar that has stopped syncing is a statement
        about the whole schedule, not about one deadline.
      */}
      {failures.length > 0 ? (
        <div className="rounded-card border border-danger/40 bg-danger/5 p-3">
          <h2 className="text-h3 font-semibold text-danger">
            {failures.length} record{failures.length === 1 ? '' : 's'} could not reach the calendar
          </h2>
          <p className="mb-2 text-micro text-text-secondary">
            Five attempts were made. The in-app reminders below are unaffected — only the shared
            calendar entry is missing.
          </p>
          <ul className="space-y-1.5">
            {failures.map((f) => (
              <li
                key={`${f.entityType}:${f.entityId}`}
                className="flex flex-wrap items-center justify-between gap-2 rounded-card bg-white px-2.5 py-1.5"
              >
                <span className="text-micro text-text-primary">
                  <span className="font-semibold">{f.label ?? f.entityType}</span>
                  {f.lastError ? (
                    <span className="text-text-secondary"> — {f.lastError}</span>
                  ) : null}
                </span>
                <button
                  type="button"
                  onClick={() => void retry(f)}
                  disabled={busy === `${f.entityType}:${f.entityId}`}
                  className="rounded-card bg-royal px-2.5 py-1 text-micro font-semibold text-white disabled:opacity-50"
                >
                  {busy === `${f.entityType}:${f.entityId}` ? 'Retrying…' : 'Retry sync'}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {!calendarConfigured ? (
        <p className="rounded-card bg-surface px-3 py-2 text-micro text-text-secondary">
          The shared calendar is not connected, so reminders are delivered in-app only.
        </p>
      ) : null}

      {loading ? (
        <p className="text-micro text-text-secondary">Reading the schedule…</p>
      ) : deadlines.length === 0 ? (
        <div className="rounded-card border border-border bg-white p-6 text-center">
          <p className="text-body text-text-primary">Nothing scheduled in this window.</p>
          <p className="text-micro text-text-secondary">
            Reminders appear here as soon as a bid date, delivery date or expiry is entered.
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {deadlines.map((d) => {
            const overdue = Date.parse(d.dueAt) < Date.now();
            const soon = Date.parse(d.dueAt) - Date.now() < 2 * DAY * 60_000;
            return (
              <li key={d.key} className="rounded-card border border-border bg-white p-3">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="text-h3 font-semibold text-navy">{d.title}</h3>
                  <span
                    className={`text-micro font-semibold ${
                      overdue ? 'text-danger' : soon ? 'text-warning' : 'text-text-secondary'
                    }`}
                  >
                    {formatDate(d.dueAt)}
                  </span>
                </div>
                <p className="text-micro text-text-secondary">
                  {[d.label, d.projectName].filter(Boolean).join(' · ')}
                  {d.recipientCount > 0
                    ? ` · ${d.recipientCount} recipient${d.recipientCount === 1 ? '' : 's'}`
                    : null}
                </p>
                <ul className="mt-2 flex flex-wrap gap-1.5">
                  {d.rungs
                    .slice()
                    .sort((a, b) => (b.offsetMinutes ?? 0) - (a.offsetMinutes ?? 0))
                    .map((r) => (
                      <li
                        key={r.id}
                        className="rounded-pill bg-surface px-2 py-0.5 text-micro text-text-secondary"
                        title={`Fires ${formatDate(r.fireAt)}`}
                      >
                        {leadLabel(r.offsetMinutes)} · {whenLabel(r.fireAt)}
                      </li>
                    ))}
                </ul>
              </li>
            );
          })}
        </ul>
      )}

      <div>
        <button
          type="button"
          onClick={() => void openLadders()}
          aria-expanded={showLadders}
          className="text-micro font-semibold text-royal underline"
        >
          {showLadders ? 'Hide reminder ladders' : 'Show reminder ladders'}
        </button>
        {showLadders && ladders ? (
          <div className="mt-2 space-y-2">
            <p className="text-micro text-text-secondary">
              Lead times per deadline type. Calendar rungs become Google reminder overrides; engine
              rungs become in-app notifications.
            </p>
            {ladders.classes.map((c) => {
              const rungs = ladders.rungs.filter((r) => r.eventClass === c.eventClass);
              if (rungs.length === 0) return null;
              return (
                <div key={c.eventClass} className="rounded-card border border-border bg-white p-2.5">
                  <h4 className="text-micro font-semibold text-navy">
                    {c.label}
                    {c.dormant ? (
                      <span className="ml-2 rounded-pill bg-surface px-2 py-0.5 font-normal text-text-secondary">
                        not in use yet
                      </span>
                    ) : null}
                  </h4>
                  {/*
                    Split by surface. Interleaved, the two ladders read as
                    duplicated lead times — "1 day before" twice looks like a
                    mistake until you notice one is an email and one is a push.
                  */}
                  {(['calendar', 'engine'] as const).map((surface) => {
                    const forSurface = rungs.filter((r) => r.surface === surface);
                    if (forSurface.length === 0) return null;
                    return (
                      <div key={surface} className="mt-1 flex flex-wrap items-baseline gap-1.5">
                        <span className="w-16 shrink-0 text-micro text-text-secondary">
                          {surface === 'calendar' ? 'Calendar' : 'In-app'}
                        </span>
                        {forSurface
                          .slice()
                          .sort((a, b) => b.offsetMinutes - a.offsetMinutes)
                          .map((r) => (
                            <span
                              key={r.id}
                              className={`rounded-pill bg-surface px-2 py-0.5 text-micro text-text-secondary ${
                                r.isActive ? '' : 'line-through opacity-60'
                              }`}
                            >
                              {leadLabel(r.offsetMinutes)}
                              {surface === 'calendar' ? ` · ${r.method}` : ''}
                            </span>
                          ))}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </section>
  );
}
